/*RegisterPage.js*/
import React, { useState, useEffect, useRef} from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { getQueryParam } from '../utils/parseQuery';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { tryFlushGuestSaveQueueBeforeAuth } from '../utils/guestOfflineQueue';
import { completeAuthSession } from '../utils/completeAuthSession';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import AuthShell from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';
import AuthAlert from '../components/auth/AuthAlert';
import styles from '../components/auth/authUi.module.css';
/*import useInsightsPrefetch from '../hooks/useInsightsPrefetch';*/



const RegisterPage = ({ setAuth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [resendMessage, setResendMessage] = useState('');
  const { showSuccess, showError, showInfo } = useToast();
  const verificationInputRefs = useRef([]);

  useEffect(() => {
    // Check for cardId in query params (legacy support)
    const activationCardId = getQueryParam('cardId');
    if (activationCardId) {
      localStorage.setItem('activationCardId', activationCardId);
    }
    
    // If we have activationCardId in localStorage but no returnUrl in state,
    // construct the returnUrl from the activationCardId
    // A pending activation with no returnUrl is recovered in handleSubmit below,
    // which rebuilds `/nfc/:cardId` from the stored card ID.
  }, [location.state]);
  
// Handle registration form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;

  try {
    setIsSubmitting(true);
    const response = await api.post('/api/auth/register', {
      email,
      password,
      acceptedTerms: true,
    });
    showSuccess(response.data.message);
    // Move to verification step automatically after a brief delay
    setTimeout(() => {
      localStorage.setItem('verificationPending', 'true');
      setShowVerification(true);
    }, 1000);
  } catch (error) {
    console.error('Registration failed:', error.response ? error.response.data : error.message);
    
    // Handle network errors
    if (!error.response) {
      showError('Unable to connect to the server. Please check your internet connection and try again.');
      return;
    }
    
    // Handle different error response formats with user-friendly messages
    let errorMessage = 'An unexpected error occurred. Please try again.';
    if (error.response && error.response.data) {
      if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors array - format them nicely
        const validationErrors = error.response.data.errors.map(err => {
          // Make validation messages more user-friendly
          const msg = err.msg || '';
          if (msg.includes('email')) {
            return 'Please enter a valid email address.';
          } else if (msg.includes('password')) {
            return 'Password must be at least 8 characters with uppercase, lowercase, and a number.';
          } else if (msg.toLowerCase().includes('terms') || msg.toLowerCase().includes('16')) {
            return 'Confirm that you are at least 16 and agree to the Terms of Service.';
          }
          return msg;
        });
        errorMessage = validationErrors.join(' ');
      } else if (error.response.data.error) {
        // Use the error message directly - backend messages are already user-friendly
        errorMessage = error.response.data.error;
      }
    }
    
    // Show error without generic prefix - backend messages are already clear
    showError(errorMessage);
  } finally {
    setIsSubmitting(false);
  }
};
const handleVerify = async () => {
  try {
    await tryFlushGuestSaveQueueBeforeAuth();
    const response = await api.post('/api/auth/verify-email', { email, code: verificationCode });
    localStorage.removeItem('verificationPending');
    showSuccess('Email verified successfully! Welcome to VELOX Network.');
    completeAuthSession({
      authResponse: response,
      setAuth,
      navigate,
      location,
      showInfo,
      emailFallback: email,
    });
  } catch (error) {
    // Handle network errors
    if (!error.response) {
      showError('Unable to connect to the server. Please check your internet connection and try again.');
      return;
    }
    
    // Handle verification errors with user-friendly messages
    const errorMessage = error.response?.data?.error || 'An unexpected error occurred. Please try again.';
    
    // Backend messages are already user-friendly, use them directly
    showError(errorMessage);
  }
};

const [cooldown, setCooldown] = useState(false);

const handleResendCode = async () => {
  if (cooldown) return;

  try {
    await api.post('/api/auth/resend-verification-code', { email });
    setResendMessage('Verification code resent successfully.');

    setCooldown(true);
    setTimeout(() => setCooldown(false), 30000); // 30 sec cooldown
  } catch (error) {
    // Handle network errors
    if (!error.response) {
      setResendMessage('Unable to connect to the server. Please check your internet connection and try again.');
      return;
    }
    
    // Handle specific error cases
    const errorMessage = error.response?.data?.error || 'Unable to resend verification code. Please try again.';
    setResendMessage(errorMessage);
  }
};

// Handle individual verification code input changes
const handleVerificationCodeChange = (e, index) => {
  const value = e.target.value;
  
  // Only allow numeric input
  if (!/^\d*$/.test(value)) return;
  
  // Update the verification code array
  const newCode = verificationCode.split('');
  newCode[index] = value;
  setVerificationCode(newCode.join(''));
  
  // Auto-focus next input if current input has a value
  if (value && index < 5) {
    verificationInputRefs.current[index + 1]?.focus();
  }
};

// Handle key down events for verification code inputs
const handleVerificationKeyDown = (e, index) => {
  // Handle backspace
  if (e.key === 'Backspace') {
    if (!verificationCode[index] && index > 0) {
      // If current input is empty, focus previous input
      verificationInputRefs.current[index - 1]?.focus();
    }
  }
  
  // Handle arrow keys
  if (e.key === 'ArrowLeft' && index > 0) {
    verificationInputRefs.current[index - 1]?.focus();
  }
  if (e.key === 'ArrowRight' && index < 5) {
    verificationInputRefs.current[index + 1]?.focus();
  }
};

// Handle paste event for verification code
const handleVerificationPaste = (e) => {
  e.preventDefault();
  const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
  
  if (pastedData.length > 0) {
    setVerificationCode(pastedData);
    
    // Focus the next empty input or the last input
    const nextEmptyIndex = Math.min(pastedData.length, 5);
    verificationInputRefs.current[nextEmptyIndex]?.focus();
  }
};

useEffect(() => {
  if (showVerification) {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => navigate('/register', { replace: true });
    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }
}, [showVerification, navigate]);

if (showVerification) {
  return (
    <AuthShell
      title="Confirm your email"
      subtitle={`We sent a 6-digit code to ${email}. Enter it below to finish setting up your account.`}
    >
      <div className={styles.codeRow}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <input
            key={index}
            type="text"
            className={styles.codeInput}
            value={verificationCode[index] || ''}
            onChange={(e) => handleVerificationCodeChange(e, index)}
            onKeyDown={(e) => handleVerificationKeyDown(e, index)}
            onPaste={handleVerificationPaste}
            maxLength="1"
            inputMode="numeric"
            pattern="[0-9]"
            autoComplete="one-time-code"
            aria-label={`Verification code digit ${index + 1}`}
            ref={(el) => {
              if (verificationInputRefs.current) {
                verificationInputRefs.current[index] = el;
              }
            }}
          />
        ))}
      </div>

      <button onClick={handleVerify} className={styles.submit} type="button">
        Verify email
      </button>

      <p className={styles.resendRow}>
        Didn&apos;t receive a code?{' '}
        <button onClick={handleResendCode} className={styles.textButton} disabled={cooldown} type="button">
          {cooldown ? 'Please wait…' : 'Resend code'}
        </button>
      </p>
      {resendMessage && <p className={styles.resendStatus}>{resendMessage}</p>}
    </AuthShell>
  );
}

return (
  <AuthShell
    title="Create your account"
    footer={
      <>
        Already have an account? <Link to="/login" state={location.state}>Sign in</Link>
      </>
    }
  >
    {location.state?.termsNotice ? (
      <AuthAlert>
        Confirm that you are at least 16 and agree to the Terms of Service to create your account.
      </AuthAlert>
    ) : null}
    <form onSubmit={handleSubmit} className={styles.form}>
      <AuthField
        id="email"
        name="email"
        label="Email"
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="username email"
        autoFocus
        spellCheck="false"
        autoCorrect="off"
        autoCapitalize="off"
        inputMode="email"
      />

      <AuthField
        id="password"
        name="password"
        label="Password"
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="new-password"
        requirementHint="Needs 8+ characters, mixed case, and a number."
        minLength="8"
        pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?!.*\s).{8,}$"
        title="Password must be at least 8 characters with uppercase, lowercase, and a number"
      />

      <button type="submit" className={styles.submit} disabled={isSubmitting}>
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </button>

      <p className={styles.cardNote}>
        By creating an account, you confirm that you are at least 16 and agree to the{' '}
        <a href="/policy/terms" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>
        .
      </p>
    </form>

    <SocialAuthButtons
      setAuth={setAuth}
      navigate={navigate}
      location={location}
      showInfo={showInfo}
      onError={(message) => showError(message)}
      mode="signup"
    />
  </AuthShell>
);
};

export default RegisterPage;
